import React, { useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import usePublicApi from 'public-api';
import { getFieldErrors } from 'public-api/utils';
import authRoutes from 'routes/codegen/authRoutes';
import { useAsyncFunction } from 'stores/hooks';
import AuthCard from 'ui-brand/components/AuthCard';
import Button from 'ui-brand/components/Button';
import PasswordInput from 'ui-brand/components/PasswordInput';

type RouteParams = {
  uidb64?: string;
  token: string;
};

type Errors = {
  new_password1?: string;
  new_password2?: string;
  token?: string;
  uid?: string;
  non_field_errors?: string;
  account_blocked?: string;
};

function PasswordSet() {
  const { uidb64: uid, token } = useParams<RouteParams>();
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [isSuccessful, setIsSuccessful] = useState(false);
  const publicApi = usePublicApi();
  const loginUrl = authRoutes.authLogin({ lang: window.appState.lang });
  const invalidLinkError = errors.token || errors.uid || errors.non_field_errors;

  const send = async () => {
    setErrors({});
    try {
      await publicApi.AuthPasswordSetApi.create({
        request: { new_password1: password1, new_password2: password2, uid: uid ?? '', token: token ?? '' },
      });
      setIsSuccessful(true);
    } catch (error) {
      setErrors(await getFieldErrors(error));
    }
  };
  const [handleSend, isSending] = useAsyncFunction(send);

  if (isSuccessful) {
    return (
      <AuthCard
        title={gettext('Password has been set')}
        subtitle={gettext('You can now sign in with your new password.')}
      >
        <p className="mt-8 text-center text-base">
          <Link to={loginUrl} className="font-semibold text-brand-500">
            {gettext('Sign in')}
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={gettext('Set a new password')} subtitle={gettext('Choose a new password for your account.')}>
      <form
        className="mt-8 flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          handleSend();
        }}
      >
        <PasswordInput
          label={gettext('New password')}
          name="password"
          placeholder={gettext('Create a new password')}
          autoComplete="new-password"
          value={password1}
          onChange={setPassword1}
          error={errors.new_password1}
        />
        <PasswordInput
          label={gettext('New password again')}
          name="password-confirmation"
          placeholder={gettext('Repeat the new password')}
          autoComplete="new-password"
          value={password2}
          onChange={setPassword2}
          error={errors.new_password2}
        />
        {errors.account_blocked && (
          <p role="alert" className="text-center text-sm text-red-600">
            {errors.account_blocked}
          </p>
        )}
        {invalidLinkError && (
          <p role="alert" className="text-center text-sm text-red-600">
            {gettext('This password reset link is no longer valid.')}{' '}
            <Link to={authRoutes.authPasswordReset({ lang: window.appState.lang })} className="font-semibold underline">
              {gettext('Request a new one')}
            </Link>
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" fullWidth loading={isSending}>
          {gettext('Save new password')}
        </Button>
      </form>
      <p className="mt-8 text-center text-base">
        <Link to={loginUrl} className="font-semibold text-brand-500">
          {gettext('Back to sign in')}
        </Link>
      </p>
    </AuthCard>
  );
}

export default PasswordSet;
